import classNames from 'classnames'
import React, {useEffect} from 'react'
import {connect} from 'react-redux'
import {createStructuredSelector} from 'reselect'

import * as pdpActions from '../../../actions/productDetailPage'
import {
  SELECTED_RENTAL_ARRIVAL_DAY,
  TRANSACTION_TYPE_BUY,
  TRANSACTION_TYPE_RENT,
  TRANSACTION_TYPE_RENT_ADD,
} from '../../../const'
import {formatPrice} from '../../../utils'

import * as cross from '../../../store/cross'
import * as pdp from '../../../store/productDetailPage'
import * as track from '../../../track/track'

import checkMark from '../../../../../svg/check-mark.svg'
import Svg from '../../Svg'

import styles from './styles.module.scss'

interface Props {
  isButtonEnabled: boolean
  price: string
  productCategorySlug: string
  productIsRentable: boolean
  productName: string
  selectedTransactionType: string
  setTransactionType: (transactionType: string) => void
  soldOutEndDateForBuy: string
  soldOutPeriodForRent: string
  transactionType: string
  showBuyPathExperiment?: boolean
}

export const TransactionTypeButton: React.FC<Props> = ({
  isButtonEnabled,
  price,
  productCategorySlug,
  productIsRentable,
  productName,
  selectedTransactionType,
  setTransactionType,
  soldOutPeriodForRent,
  soldOutEndDateForBuy,
  transactionType,
  showBuyPathExperiment,
}) => {
  const paramsByTransactionType = {
    [TRANSACTION_TYPE_RENT as string]: {
      transactionTypeText: 'Rent',
      explanatoryText: `Arrives ${SELECTED_RENTAL_ARRIVAL_DAY} before you need it.`,
      soldOutText: 'Rent\u2014Sold Out',
      soldOutDetail: `Unavailable from ${soldOutPeriodForRent}`,
      notOfferedText: 'Not available for rent',
      isSoldOut: soldOutPeriodForRent,
    },
    [TRANSACTION_TYPE_BUY as string]: {
      transactionTypeText: showBuyPathExperiment ? 'Buy New' : 'Buy',
      explanatoryText: 'Ships in 2-3 business days.',
      soldOutText: 'Buy\u2014Sold Out',
      soldOutDetail: `Estimated back in stock on ${soldOutEndDateForBuy}`,
      notOfferedText: 'Not available for purchase',
      isSoldOut: soldOutEndDateForBuy,
    },
    [TRANSACTION_TYPE_RENT_ADD as string]: {
      transactionTypeText: 'Rent',
      explanatoryText: 'Will ship with the rest of your rental order',
      soldOutText: 'Rent\u2014Sold Out',
      soldOutDetail: `Unavailable from ${soldOutPeriodForRent}`,
      notOfferedText: 'Not available for rent',
      isSoldOut: soldOutPeriodForRent,
    },
  }

  useEffect(() => {
    if (showBuyPathExperiment) {
      setTransactionType(transactionType)
    }
  }, [showBuyPathExperiment, setTransactionType, transactionType])

  const {
    transactionTypeText,
    explanatoryText,
    soldOutText,
    soldOutDetail,
    notOfferedText,
    isSoldOut,
  } = paramsByTransactionType[transactionType]

  let mainText
  let detailText

  // Item is neither in stock nor sold out for the transaction type
  const isNotOffered = !isButtonEnabled && !isSoldOut
  const disableRentButton =
    (transactionType === 'rent' || transactionType === 'rent_add') && !productIsRentable

  if (disableRentButton) {
    mainText = transactionTypeText
    detailText = notOfferedText
  } else if (isButtonEnabled) {
    mainText = transactionTypeText
    detailText = explanatoryText
  } else if (isSoldOut) {
    mainText = soldOutText
    detailText = soldOutDetail
  } else {
    mainText = notOfferedText
  }

  const isSelected = selectedTransactionType === transactionType

  const onClick = () => {
    track.pdpTransactionTypeClicked({productCategorySlug, productName, transactionType})
    setTransactionType(transactionType)
  }

  if (showBuyPathExperiment) {
    return (
      <div className={styles.buy}>
        <span className={styles.price}>
          <b>${formatPrice(price)}</b>
        </span>
        <span className={styles.text}>
          <b>{mainText}</b>
        </span>
        {detailText && <span className={styles.detail}>{detailText}</span>}
      </div>
    )
  }
  return (
    <button
      className={classNames(styles.button, {
        [styles['not-offered']]: isNotOffered,
        [styles.selected]: isSelected,
      })}
      data-test-id="transaction-type-button"
      disabled={!isButtonEnabled || disableRentButton}
      onClick={onClick}
    >
      {isButtonEnabled && !disableRentButton && (
        <span className={styles.price}>${formatPrice(price)}</span>
      )}
      <span className={styles.text}>{mainText}</span>
      {isSelected && <Svg className={styles.checkmark} src={checkMark} />}
      {detailText && <span className={styles.detail}>{detailText}</span>}
    </button>
  )
}

const mapStateToProps = createStructuredSelector({
  isButtonEnabled: cross.isTransactionTypeButtonEnabled,
  price: pdp.productPriceForTransactionType,
  productCategorySlug: pdp.productCategorySlug,
  productName: pdp.productName,
  productIsRentable: pdp.productIsRentable,
  selectedTransactionType: cross.selectedTransactionType,
  soldOutPeriodForRent: cross.soldOutPeriodForRent,
  soldOutEndDateForBuy: cross.soldOutEndDateForBuy,
})

const mapDispatchToProps = {
  setTransactionType: pdpActions.selectTransactionType,
}

export default connect(mapStateToProps, mapDispatchToProps)(TransactionTypeButton)
